/**
 * Una tarjeta que ya se vio en una habitación se queda ahí.
 * El estado en vivo (refresh cada 5s, socket) puede cambiar encendido o
 * temperatura, pero no puede sacar el equipo de la habitación.
 * Solo un listado del servidor con habitación numérica nueva la mueve,
 * o un desasignar/borrar explícito.
 */

export interface RoomCardBase {
  id: number;
  habitacion_id?: number | null;
  enabled?: boolean;
  nombre?: string;
}

interface LockedCard<T extends RoomCardBase> {
  habitacionId: number;
  device: T;
}

function roomNumber(value: unknown): number | null {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string' && value !== '' && Number.isFinite(Number(value))) {
    return Number(value);
  }
  return null;
}

export function createRoomMembership<T extends RoomCardBase>() {
  const locks = new Map<number, LockedCard<T>>();

  /** Listado completo: mueve la tarjeta solo si viene una habitación numérica. */
  function lockFromList(device: T): void {
    const id = Number(device?.id);
    const room = roomNumber(device?.habitacion_id);
    if (!Number.isFinite(id) || room == null) return;
    const prev = locks.get(id)?.device;
    locks.set(id, {
      habitacionId: room,
      device: {
        ...(prev || {}),
        ...device,
        id,
        habitacion_id: room,
      } as T,
    });
  }

  /**
   * La habitación abierta vuelve a pedir este id.
   * Si todavía no está fijado, queda en esa habitación.
   * Si ya estaba en otra, no se lo roba.
   */
  function ensureInRoom(roomId: number, device: Partial<T> & { id: number }): boolean {
    const id = Number(device?.id);
    const room = Number(roomId);
    if (!Number.isFinite(id) || !Number.isFinite(room)) return false;
    const existing = locks.get(id);
    if (existing && existing.habitacionId !== room) return false;
    const prev = existing?.device;
    if (!prev && !device.nombre) return false;
    const enabled = existing ? existing.device.enabled !== false : device.enabled !== false;
    locks.set(id, {
      habitacionId: room,
      device: {
        ...(prev || {}),
        ...device,
        id,
        habitacion_id: room,
        enabled,
        nombre: device.nombre || prev?.nombre || '',
      } as T,
    });
    return true;
  }

  function unlock(id: number): void {
    locks.delete(Number(id));
  }

  function cardsInRoom(roomId: number, live: Record<number, T>): T[] {
    const wanted = Number(roomId);
    if (!Number.isFinite(wanted)) return [];
    const out: T[] = [];
    const seen = new Set<number>();

    locks.forEach((lock, id) => {
      if (lock.habitacionId !== wanted) return;
      if (lock.device.enabled === false) return;
      const current = live[id];
      const merged = { ...lock.device } as T;
      if (current) {
        Object.keys(current).forEach((key) => {
          if (
            key === 'id'
            || key === 'habitacion_id'
            || key === 'enabled'
            || key === 'nombre'
            || key === 'device_kind'
            || key === 'ui_family'
            || key === 'orden'
            || key === 'artefacto'
            || key === 'artefacto_id'
            || key === 'paired'
          ) {
            return;
          }
          const value = (current as Record<string, unknown>)[key];
          if (value !== undefined && value !== null) {
            (merged as Record<string, unknown>)[key] = value;
          }
        });
      }
      merged.id = id;
      merged.habitacion_id = lock.habitacionId;
      merged.enabled = true;
      if (lock.device.nombre) merged.nombre = lock.device.nombre;
      out.push(merged);
      seen.add(id);
    });

    Object.values(live).forEach((device) => {
      const id = Number(device?.id);
      if (!Number.isFinite(id) || seen.has(id)) return;
      if (Number(device.habitacion_id) === wanted && device.enabled !== false) {
        out.push(device);
      }
    });

    return out;
  }

  return { lockFromList, ensureInRoom, unlock, cardsInRoom };
}
